import React, { useEffect, useMemo, useState } from 'react';
import Box from '@components/Atoms/Box';
import ShowIf from '@components/Atoms/ShowIf';
import { useGetMultipleQuestionDetails } from 'chalktalk-react/hooks/practiceHooks';
import MathJaxItem from '@components/MathJaxContainer';
import { DataGrid } from '@mui/x-data-grid';
import TableLoader from '@components/LoadingPlaceholder/TableLoader';
import { Chip } from '@mui/material';
import { useGetAssessmentNodeQuestions } from 'chalktalk-react/hooks/genericSkillHooks';

const LegacyQuestionTable = ({
  selectedUnit,
  selectedLesson,
  rowButton,
  statUpdate,
  isOLP = false,
  assessmentNodeId = undefined,
  curriculumId = undefined,
}: {
  selectedUnit;
  selectedLesson;
  rowButton: (questionId: string, eqId: number | undefined) => any;
  statUpdate: (stats: any) => void;
  isOLP: boolean;
  assessmentNodeId?: number;
  curriculumId?: number;
}) => {
  const { data: assessmentNodeQuestions } = useGetAssessmentNodeQuestions(assessmentNodeId, curriculumId);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const practiceKey = useMemo(
    () => (isOLP ? 'olp_practices' : 'legacy_practices'),
    [isOLP],
  );

  const questionNodeMap = useMemo(() => {
    const nodeMap = {};
    if (assessmentNodeId) {
      return nodeMap;
    }
    const lessons = selectedLesson ? [selectedLesson] : selectedUnit.lessons;
    lessons.forEach((l) => {
      l.assessment_nodes.forEach((node) => {
        const parentName = node?.parent?.name || '';
        nodeMap[node.id] = `${parentName} - ${node.name}`;
      });
    });
    return nodeMap;
  }, [assessmentNodeId, selectedLesson, selectedUnit.lessons]);

  const examQuestionIds = useMemo(() => {
    if (assessmentNodeId) {
      return assessmentNodeQuestions;
    }
    const eqMap = {};
    const lessons = selectedLesson ? [selectedLesson] : selectedUnit.lessons;
    lessons.forEach((l) => {
      (l?.[practiceKey] || []).forEach((eq) => {
        eqMap[eq.id] = Number(eq.question_id);
      });
    });
    return eqMap;
  }, [
    practiceKey,
    selectedLesson,
    selectedUnit.lessons,
    assessmentNodeId,
    assessmentNodeQuestions,
  ]);

  const examQuestionPage = useMemo(() => {
    const { page } = paginationModel;
    if (assessmentNodeId) {
      return Object.values(assessmentNodeQuestions).slice(page * 10, (page + 1) * 10);
    }
    const keys = Object.keys(examQuestionIds).slice(page * 10, (page + 1) * 10);
    return keys.map((key) => examQuestionIds[key]);
  }, [examQuestionIds, paginationModel, assessmentNodeId, assessmentNodeQuestions]);

  const { data: questionDetails, isLoading: questionIsLoading } =
    useGetMultipleQuestionDetails(examQuestionPage, examQuestionIds);

  const cleanHtml = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Select all img and svg elements
    const elementsToRemove = doc.querySelectorAll('img, svg');
    elementsToRemove.forEach((el) => el.remove());

    // remove &nbsp; \n and <br /> tags
    const text = doc.body?.textContent
      ?.replace(/&nbsp;/g, ' ')
      ?.replace(/\n/g, '')
      ?.replace(/<br \/>/g, '');
    if (!text) {
      return '';
    }
    return text;
  };

  useEffect(() => {
    statUpdate({ nbHits: Object.keys(examQuestionIds).length });
  }, [examQuestionIds, statUpdate]);

  const columnDefs = [
    { field: 'id', headerName: 'ID', width: 100 },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: ({ row }) => rowButton(row.action.questionId, row.action.eqId),
    },
    {
      field: 'prompt',
      headerName: 'Prompt',
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: '100%',
          }}
        >
          <MathJaxItem data={params.value} />
        </Box>
      ),
    },
    {
      field: 'nodes',
      headerName: 'Nodes',
      flex: 1,
      renderCell: ({ row }) =>
        row.nodes
          .map((node) => questionNodeMap[node])
          .filter(Boolean)
          .map((node) => <Chip key={node} label={node} size="small" variant="filled" />),
    },
  ];

  const rows = useMemo(
    () =>
      (questionDetails || []).filter(Boolean).map((eq) => ({
        id: eq?.exam_question?.question?.id,
        action: {
          eqId: eq?.id,
          questionId: eq?.exam_question?.question?.id,
        },
        prompt: cleanHtml(eq?.exam_question?.question?.prompt?.text ?? ''),
        nodes: eq?.exam_question?.question?.nodes,
      })),
    [questionDetails],
  );

  return (
    <Box>
      <ShowIf If={questionIsLoading}>
        <TableLoader />
      </ShowIf>
      <ShowIf If={!questionIsLoading}>
        <DataGrid
          rows={rows}
          rowCount={Object.keys(examQuestionIds).length}
          columns={columnDefs}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          loading={questionIsLoading}
        />
      </ShowIf>
    </Box>
  );
};

export default LegacyQuestionTable;
